import { useState } from 'react'
import { X, Plus, Trash2, Sun, Moon, AlertTriangle } from 'lucide-react'
import { todayStr } from '../../utils/dateFilters'
import { FUEL_COLORS } from '../../utils/formatters'

const EXPENSE_CATEGORIES = ['PMS to gen', 'AGO to gen', 'Oil to gen', 'Generator repair', 'Weight and measure', 'State signage agency', 'Salary advance', 'Staff salary', 'Staff loan', 'Staff food', 'Marketing', 'Water', 'NEPA bill', 'Miscellaneous']
// Stations set their own excess — any value >= 0, no upper bound.
const cleanExcess = (v) => (Number.isFinite(Number(v)) ? Math.max(0, Number(v)) : 0)

function buildForm(mode, product, tanks, pumps, editing, lastComplete, dayExpenses) {
  const today = todayStr()
  const eTank = (id) => editing?.tanks.find((t) => t.tankId === id)
  const ePump = (id) => editing?.pumps.find((p) => p.pumpId === id)
  const lTank = (id) => lastComplete?.tanks.find((t) => t.tankId === id)
  const lPump = (id) => lastComplete?.pumps.find((p) => p.pumpId === id)
  return {
    date: editing?.date ?? today,
    pricePerLiter: mode === 'edit' && editing?.pricePerLiter != null ? String(editing.pricePerLiter) : '',
    tanks: tanks.map((t) => {
      const e = eTank(t.id), l = lTank(t.id)
      const opening = editing ? e?.openingLevel : l?.closingLevel
      return {
        tankId: t.id, tankNumber: t.tankNumber,
        openingLevel: opening != null ? String(opening) : '',
        closingLevel: mode === 'edit' && e?.closingLevel != null ? String(e.closingLevel) : '',
        supplyReceived: mode === 'edit' ? String(e?.supplyReceived ?? 0) : '0',
      }
    }),
    pumps: pumps.map((p) => {
      const e = ePump(p.id), l = lPump(p.id)
      const opening = editing ? e?.openingMeter : l?.closingMeter
      return {
        pumpId: p.id, pumpNumber: p.pumpNumber, name: p.name,
        openingMeter: opening != null ? String(opening) : '',
        closingMeter: mode === 'edit' && e?.closingMeter != null ? String(e.closingMeter) : '',
        excessPer20L: String(e?.excessPer20L ?? l?.excessPer20L ?? p.defaultExcessPer20L ?? 1.0),
        openingImagePath: e?.openingImagePath ?? null,
        closingImagePath: mode === 'edit' ? (e?.closingImagePath ?? null) : null,
      }
    }),
    expenses: mode === 'morning' ? [] : (dayExpenses || []).map((e) => ({ description: e.description, amount: String(e.amount) })),
  }
}

function validate(form, mode) {
  const validExcess = (v) => v !== '' && Number.isFinite(Number(v)) && Number(v) >= 0
  if (mode === 'morning') {
    for (const t of form.tanks) if (t.openingLevel === '') return `Tank ${t.tankNumber}: opening level required.`
    for (const p of form.pumps) { if (p.openingMeter === '') return `${p.name || 'Pump ' + p.pumpNumber}: opening meter required.`; if (!validExcess(p.excessPer20L)) return `${p.name || 'Pump ' + p.pumpNumber}: excess/20L must be 0 or more.` }
    return null
  }
  if (!form.pricePerLiter || Number(form.pricePerLiter) <= 0) return 'Price per litre is required.'
  for (const t of form.tanks) { if (mode === 'edit' && t.openingLevel === '') return `Tank ${t.tankNumber}: opening required.`; if (t.closingLevel === '') return `Tank ${t.tankNumber}: closing required.` }
  for (const p of form.pumps) { if (p.closingMeter === '') return `${p.name || 'Pump ' + p.pumpNumber}: closing meter required.`; if (Number(p.closingMeter) < Number(p.openingMeter)) return `${p.name || 'Pump ' + p.pumpNumber}: closing < opening.`; if (!validExcess(p.excessPer20L)) return `${p.name || 'Pump ' + p.pumpNumber}: excess/20L must be 0 or more.` }
  for (const e of form.expenses) { if (!e.description.trim()) return 'All expense categories required.'; if (e.amount === '' || Number(e.amount) < 0) return 'Expense amounts must be ≥ 0.' }
  return null
}

function toInput(form, branchId, product, mode) {
  const morning = mode === 'morning'
  return {
    branchId, product, date: form.date, session: morning ? 'morning_open' : 'complete',
    pricePerLiter: morning ? null : Number(form.pricePerLiter),
    tanks: form.tanks.map((t) => ({ tankId: t.tankId, openingLevel: Number(t.openingLevel), closingLevel: morning ? null : Number(t.closingLevel), supplyReceived: morning ? 0 : Number(t.supplyReceived) || 0 })),
    pumps: form.pumps.map((p) => ({ pumpId: p.pumpId, openingMeter: Number(p.openingMeter), closingMeter: morning ? null : Number(p.closingMeter), excessPer20L: cleanExcess(p.excessPer20L), openingImagePath: p.openingImagePath, closingImagePath: morning ? null : p.closingImagePath })),
    expenses: morning ? undefined : form.expenses.map((e) => ({ description: e.description, amount: Number(e.amount) })),
  }
}

const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none'

export default function DataEntryModal({ branch, product, editingEntry, mode, dayExpenses, getLastComplete, onSave, onUpdate, onClose }) {
  const tanks = branch.tanks.filter((t) => t.product === product)
  const pumps = branch.pumps.filter((p) => p.product === product)
  // A day needs BOTH a tank and at least one pump for the product. If either
  // is missing the branch is misconfigured (e.g. an AGO pump with no AGO tank)
  // — surface that loudly instead of rendering empty sections and saving a
  // half-useless entry.
  const missingKit = [
    tanks.length === 0 && `a ${product} tank`,
    pumps.length === 0 && `${product} pumps`,
  ].filter(Boolean)
  const lastComplete = getLastComplete(branch.id, product, todayStr())
  const [form, setForm] = useState(() => buildForm(mode, product, tanks, pumps, editingEntry, lastComplete, dayExpenses))
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const c = FUEL_COLORS[product] || FUEL_COLORS.PMS
  const Icon = mode === 'morning' ? Sun : Moon

  const setPump = (i, patch) => setForm((f) => ({ ...f, pumps: f.pumps.map((p, idx) => idx === i ? { ...p, ...patch } : p) }))
  const setTank = (i, patch) => setForm((f) => ({ ...f, tanks: f.tanks.map((t, idx) => idx === i ? { ...t, ...patch } : t) }))
  const setExpense = (i, patch) => setForm((f) => ({ ...f, expenses: f.expenses.map((e, idx) => idx === i ? { ...e, ...patch } : e) }))

  async function submit() {
    if (missingKit.length > 0) { setError(`Cannot save: this branch has no ${missingKit.join(' and no ')}. Add them under Profile → Branches first.`); return }
    const err = validate(form, mode); if (err) { setError(err); return }
    setError(''); setSaving(true)
    const input = toInput(form, branch.id, product, mode)
    if (mode === 'morning') await onSave(input)
    else await onUpdate(input, editingEntry.id)
    setSaving(false)
  }

  const title = mode === 'morning' ? 'Morning Entry' : mode === 'evening' ? 'Complete Day' : 'Edit Entry'

  return (
    <>
      <div className="fixed inset-0 z-[60] bg-black/50 animate-fadeIn" onClick={onClose} />
      <div className="fixed inset-x-0 bottom-0 sm:inset-0 sm:flex sm:items-center sm:justify-center z-[70] animate-slideUp sm:animate-fadeIn">
        <div className="bg-white w-full sm:w-[560px] rounded-t-2xl sm:rounded-2xl flex flex-col shadow-2xl" style={{ maxHeight: '92vh' }}>
          <div className="flex items-center justify-between px-4 py-3.5 border-b border-gray-100 flex-shrink-0">
            <div className="flex items-center gap-2">
              <Icon className="w-5 h-5 text-gray-500" />
              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${c.bg} ${c.text}`}>{product}</span>
              <div><h2 className="font-bold text-gray-800">{title}</h2><p className="text-xs text-gray-400">{branch.name} · {form.date}</p></div>
            </div>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500"><X className="w-4 h-4" /></button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">
            {missingKit.length > 0 && (
              <div className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-semibold">This branch has no {missingKit.join(' and no ')} set up.</p>
                  <p className="text-xs mt-1 text-amber-700">
                    An admin needs to add {missingKit.join(' and ')} for "{branch.name}" under
                    Profile → Branches → Edit before this day can be recorded.
                  </p>
                </div>
              </div>
            )}

            {/* Pumps */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Pump meter readings</h3>
              {pumps.length === 0 && (
                <p className="text-sm text-gray-400 border border-dashed border-gray-300 rounded-xl px-4 py-3">
                  No {product} pumps configured at this branch.
                </p>
              )}
              <div className="space-y-3">
                {form.pumps.map((p, i) => {
                  const o = p.openingMeter !== '' ? Number(p.openingMeter) : null
                  const cl = p.closingMeter !== '' ? Number(p.closingMeter) : null
                  const implied = mode !== 'morning' && o != null && cl != null ? cl - o : null
                  return (
                    <div key={p.pumpId} className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                      <p className="text-xs font-bold text-gray-800 mb-2">{p.name || `Pump ${p.pumpNumber}`}</p>
                      <div className="grid grid-cols-2 gap-2">
                        {mode === 'evening' ? (
                          <div><label className="text-xs text-gray-500">Opening (morning)</label><div className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-500">{o ?? '—'}</div></div>
                        ) : (
                          <div><label className="text-xs text-gray-500">Opening meter</label><input className={inputCls} type="number" value={p.openingMeter} onChange={(e) => setPump(i, { openingMeter: e.target.value })} /></div>
                        )}
                        {mode !== 'morning' && (
                          <div><label className="text-xs text-gray-500">Closing meter</label><input className={inputCls} type="number" value={p.closingMeter} onChange={(e) => setPump(i, { closingMeter: e.target.value })} /></div>
                        )}
                        <div><label className="text-xs text-gray-500">Excess /20L</label><input className={inputCls} type="number" step="0.1" value={p.excessPer20L} onChange={(e) => setPump(i, { excessPer20L: e.target.value })} /></div>
                      </div>
                      {implied != null && <p className={`mt-1.5 text-xs font-semibold ${implied < 0 ? 'text-red-600' : 'text-green-700'}`}>Implied sold: {implied.toLocaleString()} L</p>}
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Tanks */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Tank dip readings</h3>
              {tanks.length === 0 && (
                <p className="text-sm text-gray-400 border border-dashed border-gray-300 rounded-xl px-4 py-3">
                  No {product} tank configured at this branch.
                </p>
              )}
              <div className="space-y-3">
                {form.tanks.map((t, i) => (
                  <div key={t.tankId} className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                    <p className="text-xs font-bold text-gray-800 mb-2">Tank {t.tankNumber}</p>
                    <div className="grid grid-cols-2 gap-2">
                      {mode === 'evening' ? (
                        <div><label className="text-xs text-gray-500">Opening (morning)</label><div className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-500">{t.openingLevel || '—'}</div></div>
                      ) : (
                        <div><label className="text-xs text-gray-500">Opening level (L)</label><input className={inputCls} type="number" value={t.openingLevel} onChange={(e) => setTank(i, { openingLevel: e.target.value })} /></div>
                      )}
                      {mode !== 'morning' && (<>
                        <div><label className="text-xs text-gray-500">Closing level (L)</label><input className={inputCls} type="number" value={t.closingLevel} onChange={(e) => setTank(i, { closingLevel: e.target.value })} /></div>
                        <div><label className="text-xs text-gray-500">Supply received (L)</label><input className={inputCls} type="number" value={t.supplyReceived} onChange={(e) => setTank(i, { supplyReceived: e.target.value })} /></div>
                      </>)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {mode !== 'morning' && (<>
              <div>
                <label className="text-sm font-semibold text-gray-700">Price per litre (₦)</label>
                <input className={inputCls + ' mt-1'} type="number" value={form.pricePerLiter} onChange={(e) => setForm((f) => ({ ...f, pricePerLiter: e.target.value }))} placeholder="e.g. 900" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Station expenses today <span className="text-xs font-normal text-gray-400">(whole station)</span></h3>
                <div className="space-y-2">
                  {form.expenses.map((e, i) => (
                    <div key={i} className="flex gap-2">
                      <select className={inputCls} value={e.description} onChange={(ev) => setExpense(i, { description: ev.target.value })}>
                        <option value="">Category…</option>{EXPENSE_CATEGORIES.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
                      </select>
                      <input className={inputCls} type="number" placeholder="₦" value={e.amount} onChange={(ev) => setExpense(i, { amount: ev.target.value })} />
                      <button onClick={() => setForm((f) => ({ ...f, expenses: f.expenses.filter((_, idx) => idx !== i) }))} className="px-2 text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                  <button onClick={() => setForm((f) => ({ ...f, expenses: [...f.expenses, { description: '', amount: '' }] }))} className="flex items-center gap-1 text-xs text-gray-500 border border-dashed border-gray-300 rounded-lg px-3 py-2 w-full justify-center"><Plus className="w-3 h-3" />Add expense</button>
                </div>
              </div>
            </>)}

            {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5">{error}</p>}
          </div>

          <div className="px-4 py-3 border-t border-gray-100 flex-shrink-0">
            <button onClick={submit} disabled={saving || missingKit.length > 0} className="w-full py-3 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-60">
              {saving ? 'Saving…' : mode === 'morning' ? 'Save Morning Entry' : mode === 'evening' ? 'Complete Day' : 'Update Entry'}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
